/**
 * Wallet Provider Proxy for Iframe (glide-deposit)
 *
 * Creates proxy EIP-1193 providers for wallets that are available in the parent window
 * but don't inject into iframes (like Rainbow wallet).
 *
 */

import { logger } from "./logger";

export interface EIP1193Provider {
  request(args: {
    method: string;
    params?: unknown[] | Record<string, unknown>;
  }): Promise<unknown>;
  on(event: string, listener: (...args: unknown[]) => void): void;
  removeListener(event: string, listener: (...args: unknown[]) => void): void;
  isConnected?: boolean;
  chainId?: string;
  selectedAddress?: string | null;
}

export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
}

export interface ProxiedProviderDetail {
  info: EIP6963ProviderInfo;
  provider: EIP1193Provider;
}

interface ProviderRpcError extends Error {
  code?: number;
  data?: unknown;
}

let requestIdCounter = 0;
const pendingRequests = new Map<
  string,
  { resolve: (value: unknown) => void; reject: (error: Error) => void }
>();
const eventListeners = new Map<
  string,
  Map<string, Set<(...args: unknown[]) => void>>
>();
const announcedProviders = new Map<string, EIP6963ProviderInfo>();
const announcedRdns = new Set<string>();
const providerCache = new Map<string, EIP1193Provider>();
const nativeProviders = new Set<string>();
let bridgeInitialized = false;

/**
 * Initialize wallet provider proxy in iframe
 * Listens for provider announcements from parent and creates proxy providers
 */
export function initializeIframeBridge(parentOrigin: string) {
  if (bridgeInitialized) {
    return;
  }
  bridgeInitialized = true;

  // Track native providers that are already in the iframe before we start
  window.addEventListener("eip6963:announceProvider", (event: Event) => {
    const customEvent = event as CustomEvent<{
      info: EIP6963ProviderInfo;
      provider: EIP1193Provider;
    }>;
    nativeProviders.add(customEvent.detail.info.rdns);
  });
  window.dispatchEvent(new Event("eip6963:requestProvider"));

  const isValidOrigin = (origin: string): boolean => {
    return origin === parentOrigin;
  };

  const announceProvider = (
    info: EIP6963ProviderInfo,
    initialState?: {
      isConnected?: boolean;
      chainId?: string;
      selectedAddress?: string;
    },
  ) => {
    // Skip if already announced (by UUID or RDNS)
    if (announcedProviders.has(info.uuid) || announcedRdns.has(info.rdns)) {
      return;
    }

    // Skip if this provider already exists natively in iframe
    if (nativeProviders.has(info.rdns)) {
      return;
    }

    announcedProviders.set(info.uuid, info);
    announcedRdns.add(info.rdns);

    // Create or get cached proxy provider
    let proxyProvider = providerCache.get(info.uuid);
    if (!proxyProvider) {
      proxyProvider = createProxyProvider(
        info.uuid,
        parentOrigin,
        initialState,
      );
      providerCache.set(info.uuid, proxyProvider);
    }

    // Announce provider using EIP-6963 standard
    window.dispatchEvent(
      new CustomEvent("eip6963:announceProvider", {
        detail: {
          info,
          provider: proxyProvider,
        },
      }),
    );
  };

  // Listen for messages from parent
  window.addEventListener("message", (event) => {
    if (!isValidOrigin(event.origin)) return;

    const { type } = event.data;

    switch (type) {
      case "glide:providers": {
        // Parent window sent us list of available providers
        // (Rainbow or other wallet providers that don't inject into iframes)
        // Create proxy for each and announce via EIP-6963
        const { providers } = event.data;
        providers.forEach(
          (providerData: {
            info: EIP6963ProviderInfo;
            isConnected?: boolean;
            chainId?: string;
            selectedAddress?: string;
          }) => {
            announceProvider(providerData.info, {
              isConnected: providerData.isConnected,
              chainId: providerData.chainId,
              selectedAddress: providerData.selectedAddress,
            });
          },
        );
        break;
      }

      case "glide:providerResponse": {
        // Response from parent for a provider request we made
        // Match it to pending request and resolve/reject the promise
        const { requestId, result, error, code, data } = event.data;
        const pending = pendingRequests.get(requestId);

        if (pending) {
          pendingRequests.delete(requestId);
          if (error) {
            // Recreate error with all properties
            const err = new Error(error) as ProviderRpcError;
            if (code !== undefined) err.code = code;
            if (data !== undefined) err.data = data;
            pending.reject(err);
          } else {
            pending.resolve(result);
          }
        }
        break;
      }

      case "glide:providerEvent": {
        const { uuid, eventName, args } = event.data;
        const listeners = eventListeners.get(uuid)?.get(eventName);

        if (listeners) {
          listeners.forEach((listener) => {
            try {
              listener(...args);
            } catch (err) {
              logger.error(`Error in event listener for ${eventName}: ${err}`);
            }
          });
        }
        break;
      }
    }
  });

  // Request providers from parent
  if (window.parent !== window) {
    window.parent.postMessage({ type: "glide:getProviders" }, parentOrigin);
  }

  // Listen for EIP-6963 provider requests and re-announce cached providers
  window.addEventListener("eip6963:requestProvider", () => {
    announcedProviders.forEach((info) => {
      const provider = providerCache.get(info.uuid);
      if (provider) {
        window.dispatchEvent(
          new CustomEvent("eip6963:announceProvider", {
            detail: {
              info,
              provider,
            },
          }),
        );
      }
    });
  });
}

/**
 * Create a proxy provider that forwards all requests to parent window
 */
function createProxyProvider(
  uuid: string,
  initialParentOrigin: string,
  initialState?: {
    isConnected?: boolean;
    chainId?: string;
    selectedAddress?: string;
  },
): EIP1193Provider {
  const getParentOrigin = () => {
    return initialParentOrigin;
  };

  const provider: EIP1193Provider = {
    // Generic request handler - forwards ANY method to parent window
    request: async ({ method, params }) => {
      return new Promise((resolve, reject) => {
        const requestId = `${uuid}_${requestIdCounter++}`;
        pendingRequests.set(requestId, { resolve, reject });

        window.parent.postMessage(
          {
            type: "glide:providerRequest",
            data: {
              uuid,
              requestId,
              method,
              params,
            },
          },
          getParentOrigin(),
        );

        // Timeout after 60 seconds (for long-running operations)
        setTimeout(() => {
          if (pendingRequests.has(requestId)) {
            pendingRequests.delete(requestId);
            reject(new Error("Request timeout"));
          }
        }, 60000);
      });
    },

    on: (eventName: string, listener: (...args: unknown[]) => void) => {
      if (!eventListeners.has(uuid)) {
        eventListeners.set(uuid, new Map());
      }
      if (!eventListeners.get(uuid)!.has(eventName)) {
        eventListeners.get(uuid)!.set(eventName, new Set());

        const requestId = `${uuid}_${requestIdCounter++}`;
        window.parent.postMessage(
          {
            type: "glide:addEventListener",
            data: {
              uuid,
              requestId,
              eventName,
            },
          },
          getParentOrigin(),
        );
      }

      eventListeners.get(uuid)!.get(eventName)!.add(listener);
    },

    removeListener: (
      eventName: string,
      listener: (...args: unknown[]) => void,
    ) => {
      const listeners = eventListeners.get(uuid)?.get(eventName);
      if (listeners) {
        listeners.delete(listener);

        if (listeners.size === 0) {
          const requestId = `${uuid}_${requestIdCounter++}`;
          window.parent.postMessage(
            {
              type: "glide:removeEventListener",
              data: {
                uuid,
                requestId,
                eventName,
              },
            },
            getParentOrigin(),
          );
        }
      }
    },
  };

  if (initialState?.isConnected !== undefined) {
    provider.isConnected = initialState.isConnected;
  }
  if (initialState?.chainId !== undefined) {
    provider.chainId = initialState.chainId;
  }
  if (initialState?.selectedAddress !== undefined) {
    provider.selectedAddress = initialState.selectedAddress;
  }

  return provider;
}
